import { attachmentFilename } from '#lib/attachmentFilename';
import { fetcher } from '#lib/backendFetcher';
import { filtersToQueryParams } from '#lib/tableFilters';

const BASE = '/api/v1/post-mailing';

// Campaigns

/**
 * Fetches all post mailing campaigns of the user.
 * @returns {Promise<Array>}
 */
export const fetchCampaigns = async () => {
	const response = await fetcher(`${BASE}/campaigns`);
	return response.campaigns;
};

/**
 * Fetches a single post mailing campaign by ID.
 * @param {string} id - Campaign ID
 * @returns {Promise<Object>}
 */
export const fetchCampaign = async (id) => {
	return fetcher(`${BASE}/campaigns/${id}`);
};

/**
 * Creates a new post mailing campaign.
 * @param {Object} params
 * @param {string} params.name - Campaign name
 * @param {string} [params.postMailingTemplateId] - Linked template providing the design
 * @returns {Promise<Object>}
 */
export const createCampaign = async ({ name, postMailingTemplateId }) => {
	return fetcher(`${BASE}/campaigns`, {
		method: 'POST',
		body: { name, postMailingTemplateId },
	});
};

/**
 * Updates an existing post mailing campaign.
 * @param {Object} params
 * @param {string} params.id - Campaign ID
 * @param {Object} params.updates - Object with the fields to change
 * @returns {Promise<Object>}
 */
export const updateCampaign = async ({ id, updates }) => {
	return fetcher(`${BASE}/campaigns/${id}`, {
		method: 'PATCH',
		body: updates,
	});
};

/**
 * Deletes a post mailing campaign by ID.
 * @param {string} id - Campaign ID
 * @returns {Promise<Object>}
 */
export const deleteCampaign = async (id) => {
	return fetcher(`${BASE}/campaigns/${id}`, {
		method: 'DELETE',
	});
};

/**
 * Syncs the contact lists assigned to a post mailing campaign (many-to-many).
 * @param {Object} params
 * @param {string} params.campaignId - Campaign ID
 * @param {string[]} params.contactListIds - Contact list IDs to assign
 * @returns {Promise<{synced: number}>}
 */
export const syncCampaignLists = async ({ campaignId, contactListIds }) => {
	return fetcher(`${BASE}/campaigns/${campaignId}/lists`, {
		method: 'PUT',
		body: { contactListIds },
	});
};

// Cost & Sending

/**
 * Calculates a cost estimate for a post mailing campaign.
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<Object>}
 */
export const fetchCostEstimate = async (campaignId) => {
	return fetcher(`${BASE}/campaigns/${campaignId}/cost-estimate`, {
		method: 'POST',
	});
};

/**
 * Confirms and sends a post mailing campaign: freezes recipients, then either starts payment or releases
 * for free. Normal plans get a hosted Stripe Checkout session (`checkoutUrl`) to redirect to — the send is
 * released by the backend's checkout webhook once paid (card/PayPal immediately, SEPA after it clears). The
 * 'owner' plan (our own org) sends for free: the backend releases immediately and returns `released: true`.
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<{checkoutUrl?: string, released?: boolean, recipientCount: number}>}
 */
export const confirmAndSend = async (campaignId) => {
	return fetcher(`${BASE}/campaigns/${campaignId}/confirm-and-send`, {
		method: 'POST',
	});
};

/**
 * Abandons a pending payment (called when the customer returns from Stripe's cancel_url): resets an
 * awaiting_payment campaign back to draft so it can be edited/retried. Idempotent.
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<{status: string}>}
 */
export const abandonPayment = async (campaignId) => {
	return fetcher(`${BASE}/campaigns/${campaignId}/abandon-payment`, {
		method: 'POST',
	});
};

/**
 * Re-opens the payment step for an awaiting_payment campaign (e.g. the Stripe tab was closed and the
 * checkout URL lost). Exactly one of the optional fields is set: `checkoutUrl` (session still open),
 * `paymentInFlight` (session completed, webhook pending) or `expired` (campaign reset to draft).
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<{status: string, checkoutUrl?: string, paymentInFlight?: boolean, expired?: boolean}>}
 */
export const resumePayment = async (campaignId) => {
	return fetcher(`${BASE}/campaigns/${campaignId}/resume-payment`, {
		method: 'POST',
	});
};

/**
 * Resolves eBrief address warnings on a campaign in 'needs_review': the listed letters are confirmed
 * (sent), the skipped ones are cancelled, then the eBrief job advances.
 *
 * @param {Object} params
 * @param {string} params.campaignId - Campaign ID
 * @param {string[]} [params.confirm] - Send IDs to confirm (send anyway)
 * @param {string[]} [params.skip] - Send IDs to skip (cancel)
 * @returns {Promise<{confirmed: number, skipped: number}>}
 */
export const confirmWarnings = async ({ campaignId, confirm = [], skip = [] }) => {
	return fetcher(`${BASE}/campaigns/${campaignId}/confirm-warnings`, {
		method: 'POST',
		body: { confirm, skip },
	});
};

/**
 * Cancels a campaign by rolling back its eBrief job. Only allowed before distribution
 * (status sending / needs_review / ready_to_distribute).
 *
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<{status: string}>}
 */
export const cancelCampaign = async (campaignId) => {
	return fetcher(`${BASE}/campaigns/${campaignId}/cancel`, {
		method: 'POST',
	});
};

/**
 * Pulls the latest job state from eBrief and applies it (manual fallback for missed webhooks).
 *
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<{refreshed: boolean, ebriefJobStatus: string}>}
 */
export const refreshCampaignStatus = async (campaignId) => {
	return fetcher(`${BASE}/campaigns/${campaignId}/refresh-status`, {
		method: 'POST',
	});
};

// Preview PDF

/**
 * Generates a PDF preview for a post mailing campaign.
 * @param {string} campaignId - Campaign ID
 * @param {Object} [body] - `{ crmContactId }` or `{ crmCompanyId }` renders it for that recipient
 *   instead of the sample one
 * @returns {Promise<Blob>}
 */
export const generatePreviewPdf = async (campaignId, body = {}) => {
	return fetcher(`${BASE}/campaigns/${campaignId}/preview-pdf`, {
		method: 'POST',
		body,
		responseType: 'blob',
	});
};

// Sends

/**
 * Fetches a page of sends for a post mailing campaign (server-side paginated + filtered).
 * @param {string} campaignId - Campaign ID
 * @param {Object} [options]
 * @param {{status?: string[], scanned?: boolean, search?: string}} [options.filters] - Status values
 *   ('sent'|'processing'|'returned'|'failed'|'needs_review') are OR-combined; scanned false = handed to
 *   the post but not scanned; search matches the recipient name
 * @param {number} [options.page=1] - 1-based
 * @param {number} [options.pageSize=50] - Entries per page
 * @returns {Promise<{sends: Array, filteredTotal: number, page: number, pageSize: number}>}
 */
export const fetchSends = async (campaignId, { filters = {}, page = 1, pageSize = 50 } = {}) => {
	const params = new URLSearchParams({ ...filtersToQueryParams(filters), page, pageSize });
	return fetcher(`${BASE}/campaigns/${campaignId}/sends?${params}`);
};

/**
 * Fetches aggregated send statistics, per-status counts and the blocked-recipient count of a campaign.
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<{stats: Object, statusCounts: Object<string, number>, blockedCount: number}>}
 */
export const fetchSendsStats = async (campaignId) => {
	return fetcher(`${BASE}/campaigns/${campaignId}/statistics`);
};

/**
 * Fetches the campaign's recipients that were skipped because they are on the blacklist.
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<{blockedRecipients: Array<{recipientName: string, companyName: string, crmContactId: string, crmCompanyId: string}>}>}
 */
export const fetchBlockedRecipients = async (campaignId) => {
	return fetcher(`${BASE}/campaigns/${campaignId}/blocked-recipients`);
};

/**
 * Downloads the persisted PDF of a single letter (send); the filename comes from the response.
 * @param {string} campaignId - Campaign ID
 * @param {string} sendId - Send (per-recipient letter) ID
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export const downloadSendPdf = async (campaignId, sendId) => {
	const response = await fetcher(`${BASE}/campaigns/${campaignId}/sends/${sendId}/pdf`, {
		responseType: 'stream',
	});
	const filename = attachmentFilename(response.headers.get('Content-Disposition'), `Brief_${sendId}.pdf`);
	const blob = await response.blob();
	return { blob, filename };
};

/**
 * Exports a campaign's letters as CSV: explicit sends or every send matching the sends-table filters
 * (resolved by the backend).
 * @param {string} campaignId - Campaign ID
 * @param {Object} options
 * @param {string[]} options.columns - Column keys to export
 * @param {string[]} [options.sendIds] - Explicit sends; XOR with selectAll
 * @param {boolean} [options.selectAll] - Every send matching `filters` (all sends without filters)
 * @param {{status?: string[], scanned?: boolean, search?: string}} [options.filters] - Sends-table filters, only with selectAll
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export const exportCampaignToCsv = async (campaignId, options) => {
	const response = await fetcher(`${BASE}/campaigns/${campaignId}/export-csv`, {
		method: 'POST',
		body: options,
		responseType: 'stream',
	});
	const filename = attachmentFilename(response.headers.get('Content-Disposition'), 'post-mailing_export.csv');
	const blob = await response.blob();
	return { blob, filename };
};
